import { useCallback, useEffect, useState } from 'react'
import { api, Fout } from './api'
import type { FactuurAntwoord } from './types'

/* ===========================================================================
 *  Eén factuur, en de drie schermen eronder
 *
 *  Originelen, Boekingen en Tijdlijn lezen dezelfde factuur als het
 *  detail. Die wordt één keer opgehaald en bewaard zolang je erin rondkijkt;
 *  na een actie (tekenen, een opmerking) gaat hij eruit en komt hij vers.
 * =========================================================================== */

const bewaard = new Map<string, { op: number; antwoord: FactuurAntwoord }>()

/** Hoe lang een opgehaalde factuur goed blijft om tussen de schermen te wisselen. */
const VERS = 60_000

export function vergeetFactuur(id: string) { bewaard.delete(id) }

export function useFactuur(id: string) {
  const [antwoord, setAntwoord] = useState<FactuurAntwoord | null>(() => bewaard.get(id)?.antwoord ?? null)
  const [op, setOp] = useState<number>(() => bewaard.get(id)?.op ?? 0)
  const [laden, setLaden] = useState(false)
  const [fout, setFout] = useState<string | null>(null)

  const haal = useCallback(async (dwing = false) => {
    const b = bewaard.get(id)
    if (!dwing && b && Date.now() - b.op < VERS) {
      setAntwoord(b.antwoord)
      setOp(b.op)
      return
    }
    setLaden(true)
    setFout(null)
    try {
      const a = await api.factuur(id)
      const nu = Date.now()
      bewaard.set(id, { op: nu, antwoord: a })
      setAntwoord(a)
      setOp(nu)
    } catch (e) {
      setFout(e instanceof Fout ? e.message : String(e))
    } finally {
      setLaden(false)
    }
  }, [id])

  useEffect(() => {
    setAntwoord(bewaard.get(id)?.antwoord ?? null)
    void haal()
  }, [id, haal])

  return { antwoord, op, laden, fout, ververs: () => haal(true) }
}
